import { useRef, useState } from 'react';
import { cn } from '../cn';
import BackIcon from '../icons/back-icon';
import MoreIcon from '../icons/more-icon';
import NotificationIcon from '../icons/notification-icon';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Sheet, SheetContent, SheetTitle } from '../ui/sheet';
import { useShell } from '../provider';
import { NotificationMobileActions } from './notification-mobile-actions';
import {
  NotificationPanel,
  type NotificationPanelView,
} from './notification-panel';
import {
  DEFAULT_NOTIFICATION_PANEL_VIEW,
  NOTIFICATION_PREFERENCES,
  type HeaderNotificationItem,
  type HeaderNotificationSource,
  type NotificationPreference,
} from './notification-types';
import {
  HEADER_NOTIFICATION_DESKTOP_CLASS,
  HEADER_NOTIFICATION_MOBILE_CLASS,
} from './notification-styles';

type HeaderNotificationsProps = {
  initialNotifications?: HeaderNotificationItem[];
  source?: HeaderNotificationSource;
  className?: string;
};

const DEFAULT_PREFERENCE_VALUES: Record<NotificationPreference['id'], boolean> =
  {
    community: true,
    news_updates: true,
    chat: true,
    reco_job: true,
    application_status: true,
  };

const NOTIFICATION_TRACKING_EVENTS = {
  markAllAsRead: 'js_noti_mark_all_as_read',
  view: 'js_noti_view',
  visitCenter: 'js_visit_noti_center',
} as const;

export function HeaderNotifications({
  initialNotifications = [],
  source,
  className,
}: HeaderNotificationsProps) {
  const { analytics } = useShell();
  const [localNotifications, setLocalNotifications] =
    useState(initialNotifications);
  const notifications = source?.items ?? localNotifications;
  const isUnreadStateKnown = source ? source.totalUnread !== null : true;
  const hasUnread = source
    ? (source.totalUnread ?? 0) > 0
    : notifications.some(item => item.unread);
  const desktopScrollRef = useRef<HTMLDivElement>(null);
  const mobileScrollRef = useRef<HTMLDivElement>(null);
  const [desktopOpen, setDesktopOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileActionsOpen, setMobileActionsOpen] = useState(false);
  const [desktopView, setDesktopView] = useState<NotificationPanelView>(
    DEFAULT_NOTIFICATION_PANEL_VIEW
  );
  const [mobileView, setMobileView] = useState<NotificationPanelView>(
    DEFAULT_NOTIFICATION_PANEL_VIEW
  );
  const [preferenceValues, setPreferenceValues] = useState(
    DEFAULT_PREFERENCE_VALUES
  );
  const preferences = source?.preferences ?? NOTIFICATION_PREFERENCES;
  const resolvedPreferenceValues = source?.preferenceValues ?? preferenceValues;

  const selectNotification = (notification: HeaderNotificationItem) => {
    analytics.track(NOTIFICATION_TRACKING_EVENTS.view, {
      noti_info: {
        ...notification.trackingProperties,
        noti_channel: 'noti center',
      },
    });

    if (notification.unread && !source?.isMutating) {
      if (source) {
        source.onMarkRead(notification.id);
      } else {
        setLocalNotifications(currentNotifications =>
          currentNotifications.map(item =>
            item.id === notification.id ? { ...item, unread: false } : item
          )
        );
      }
    }

    if (notification.href) {
      setDesktopOpen(false);
      setMobileOpen(false);
      setMobileActionsOpen(false);
      setDesktopView(DEFAULT_NOTIFICATION_PANEL_VIEW);
      setMobileView(DEFAULT_NOTIFICATION_PANEL_VIEW);
      source?.onClose();
    }
  };

  const markAllAsRead = () => {
    analytics.track(NOTIFICATION_TRACKING_EVENTS.markAllAsRead);

    if (source) {
      source.onMarkAllRead();

      return;
    }

    setLocalNotifications(currentNotifications =>
      currentNotifications.map(notification => ({
        ...notification,
        unread: false,
      }))
    );
  };

  const updatePreference = (
    id: NotificationPreference['id'],
    value: boolean
  ) => {
    if (source) {
      source.onUpdatePreference(id, value);

      return;
    }

    setPreferenceValues(currentValues => ({ ...currentValues, [id]: value }));
  };

  const openDesktopPreferences = () => {
    source?.onLoadPreferences();
    setDesktopView('preferences');
  };

  const openMobilePreferences = () => {
    source?.onLoadPreferences();
    setMobileView('preferences');
  };

  const handleDesktopOpenChange = (open: boolean) => {
    setDesktopOpen(open);

    if (open) {
      analytics.track(NOTIFICATION_TRACKING_EVENTS.visitCenter);
      source?.onOpen();
    } else {
      source?.onClose();
    }

    if (!open) {
      setDesktopView(DEFAULT_NOTIFICATION_PANEL_VIEW);
    }
  };

  const handleMobileOpenChange = (open: boolean) => {
    setMobileOpen(open);

    if (open) {
      analytics.track(NOTIFICATION_TRACKING_EVENTS.visitCenter);
      source?.onOpen();
    } else {
      source?.onClose();
    }

    if (!open) {
      setMobileView(DEFAULT_NOTIFICATION_PANEL_VIEW);
      setMobileActionsOpen(false);
    }
  };

  return (
    <>
      <div className={cn(HEADER_NOTIFICATION_DESKTOP_CLASS, className)}>
        <Popover open={desktopOpen} onOpenChange={handleDesktopOpenChange}>
          <PopoverTrigger asChild>
            <button
              type="button"
              aria-label={
                hasUnread
                  ? 'Mở thông báo, có thông báo chưa đọc'
                  : 'Mở thông báo'
              }
              className="group text-on-surface hover:bg-surface-variant focus-visible:ring-primary/50 relative flex size-11 items-center justify-center rounded-[14px] bg-transparent transition-colors focus-visible:ring-2 focus-visible:outline-none"
            >
              <NotificationIcon size={24} aria-hidden />
              {hasUnread ? (
                <span
                  aria-hidden
                  className="bg-negative ring-surface-pure group-hover:ring-surface-variant absolute top-1.5 right-1.5 size-2.5 rounded-full ring-2"
                />
              ) : null}
            </button>
          </PopoverTrigger>
          <PopoverContent
            align="end"
            sideOffset={4}
            className="border-outline-variant bg-surface shadow-search-suggestions w-[448px] rounded-[18px] border p-4"
          >
            <div
              ref={desktopScrollRef}
              className="max-h-[600px] overflow-y-auto"
            >
              <NotificationPanel
                view={desktopView}
                items={notifications}
                groups={source?.groups}
                source={source}
                scrollRootRef={desktopScrollRef}
                preferences={preferences}
                preferenceValues={resolvedPreferenceValues}
                isMobile={false}
                onMarkAllAsRead={markAllAsRead}
                onOpenPreferences={openDesktopPreferences}
                onSelectNotification={selectNotification}
                onBack={() => setDesktopView(DEFAULT_NOTIFICATION_PANEL_VIEW)}
                onPreferenceChange={updatePreference}
              />
            </div>
          </PopoverContent>
        </Popover>
      </div>

      <div className={cn(HEADER_NOTIFICATION_MOBILE_CLASS, className)}>
        <button
          type="button"
          aria-label={
            mobileOpen
              ? 'Đóng thông báo'
              : hasUnread
                ? 'Mở thông báo, có thông báo chưa đọc'
                : 'Mở thông báo'
          }
          aria-expanded={mobileOpen}
          onClick={() => handleMobileOpenChange(!mobileOpen)}
          className="group text-on-surface hover:bg-surface-variant focus-visible:ring-primary/50 relative flex size-11 items-center justify-center rounded-[14px] bg-transparent transition-colors focus-visible:ring-2 focus-visible:outline-none"
        >
          <NotificationIcon size={24} aria-hidden />
          {hasUnread ? (
            <span
              aria-hidden
              className="bg-negative ring-surface-pure group-hover:ring-surface-variant absolute top-1.5 right-1.5 size-2.5 rounded-full ring-2"
            />
          ) : null}
        </button>
        <Sheet open={mobileOpen} onOpenChange={handleMobileOpenChange}>
          <SheetContent
            side="page"
            showOverlay={false}
            showCloseButton={false}
            aria-describedby={undefined}
            className="bg-surface-pure max-h-none gap-0 border-0 p-0 xl:hidden"
          >
            <SheetTitle className="sr-only">Thông báo</SheetTitle>
            <header className="border-outline-variant flex h-16 shrink-0 items-center justify-between gap-3 border-b px-4">
              <div className="flex min-w-0 items-center gap-3">
                <button
                  type="button"
                  onClick={() =>
                    mobileView === 'preferences'
                      ? setMobileView(DEFAULT_NOTIFICATION_PANEL_VIEW)
                      : handleMobileOpenChange(false)
                  }
                  aria-label={
                    mobileView === 'preferences'
                      ? 'Quay lại Hoạt động'
                      : 'Quay lại'
                  }
                  className="text-on-surface hover:bg-surface-variant focus-visible:ring-primary/50 flex size-10 shrink-0 items-center justify-center rounded-xl transition-colors focus-visible:ring-2 focus-visible:outline-none"
                >
                  <BackIcon size={20} aria-hidden />
                </button>
                <h1 className="font-display text-display-2xs leading-display-2xs truncate font-semibold">
                  {mobileView === 'preferences' ? 'Cài đặt' : 'Hoạt động'}
                </h1>
              </div>
              {mobileView === 'feed' ? (
                <button
                  type="button"
                  onClick={() => setMobileActionsOpen(true)}
                  aria-label="Tùy chọn thông báo"
                  className="text-on-surface hover:bg-surface-variant focus-visible:ring-primary/50 flex size-10 shrink-0 items-center justify-center rounded-xl focus-visible:ring-2 focus-visible:outline-none"
                >
                  <MoreIcon size={20} aria-hidden />
                </button>
              ) : null}
            </header>
            <div
              ref={mobileScrollRef}
              className="min-h-0 flex-1 overflow-y-auto px-4 pt-5 pb-6"
            >
              <NotificationPanel
                view={mobileView}
                items={notifications}
                groups={source?.groups}
                source={source}
                scrollRootRef={mobileScrollRef}
                preferences={preferences}
                preferenceValues={resolvedPreferenceValues}
                isMobile
                onMarkAllAsRead={markAllAsRead}
                onOpenPreferences={openMobilePreferences}
                onSelectNotification={selectNotification}
                onBack={() => setMobileView(DEFAULT_NOTIFICATION_PANEL_VIEW)}
                onPreferenceChange={updatePreference}
              />
            </div>
          </SheetContent>
        </Sheet>
        <NotificationMobileActions
          open={mobileActionsOpen}
          onOpenChange={setMobileActionsOpen}
          onMarkAllAsRead={markAllAsRead}
          canMarkAll={!source?.isMutating && hasUnread}
          hasUnread={hasUnread}
          isUnreadStateKnown={isUnreadStateKnown}
          onOpenPreferences={openMobilePreferences}
        />
      </div>
    </>
  );
}
